import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { AppointmentStatus, Contact, PatientInfo } from '../services/api-client/contact-api';
import { ILocation } from '../services/location-service/location-service';
import { FeedbackType } from '../services/patient-feedback-service/patient-feedback-service-types';
import PatientFeedbackService from '../services/patient-feedback-service/patient-feedback-service';

const AUTOCOMM_TYPE = {
  [FeedbackType.Review]: 12,
  [FeedbackType.Survey]: 13
}

export const useIsReviewSurveyEnabled = ({
  contactDetails,
  contactLocation,
  patientInfo,
  loggedInUserLocation
}: {
  contactDetails: Contact;
  contactLocation: ILocation | undefined;
  patientInfo: PatientInfo;
  loggedInUserLocation: ILocation | undefined;
}) => {
  const { config } = useConfig();

  const isReviewSurveyEnabled = useMemo(() => {
    if (!contactDetails || !contactLocation || !patientInfo || !loggedInUserLocation) {
      return false;
    }

    const isContactLocationValid = contactLocation?.isCommunicationActive && contactLocation?.accountStatus === 0;
    const sameNetworkContact = contactDetails?.networkId === loggedInUserLocation?.idNetwork;

    const contactHasAppointment =
      patientInfo?.visits?.some(
        (x) =>
          x.location.id === contactLocation.id &&
          moment(x.date).isBefore(moment()) &&
          x.status !== AppointmentStatus.Cancelled &&
          x.status !== AppointmentStatus.Deleted
      ) || false;

    if (config.contact.fullPatientProfilePageEnabled === 'true') {
      return isContactLocationValid && contactHasAppointment && contactDetails.isEmailValid;
    }

    return isContactLocationValid && contactHasAppointment && contactDetails.isEmailValid && sameNetworkContact;
  }, [
    contactDetails,
    contactLocation,
    patientInfo,
    loggedInUserLocation,
    config.contact.fullPatientProfilePageEnabled
  ]);

  return isReviewSurveyEnabled;
};

export const useCreateReviewSurvey = ({
  contactDetails,
  locationId
}: {
  contactDetails: Contact;
  locationId: number;
}) => {
  const { config } = useConfig();
  const { showError, showSuccess } = useNotification();
  const oidc = useOidcWithAdmin();

  const patientFeedbackService = useMemo(
    () => PatientFeedbackService.getInstance(config, oidc.oidcService),
    [config, oidc]
  );

  const request = async (autoCommType: number) => {
    if (config.contact.fullPatientProfilePageEnabled === 'true') {
      try {
        if (autoCommType === FeedbackType.Review) {
          await patientFeedbackService.requestReview(locationId, contactDetails.id);
        }

        if (autoCommType === FeedbackType.Survey) {
          await patientFeedbackService.requestSurvey(locationId, contactDetails.id);
        }

        showSuccess('The invitation was sent.');
      } catch {
        showError('Unable to send invitation.');
      }
    } else {
      const url = `${config.practice.url}/Patient/CreateManuallyAutomatiCommunication?idPatient=${contactDetails.id}`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ autoCommType: AUTOCOMM_TYPE[autoCommType as FeedbackType] })
        });

        const json = await response.json();

        if (!json.IsSuccess) throw new Error(json.Message);

        showSuccess('The invitation was sent.');
      } catch {
        showError('Unable to send invitation.');
      }
    }
  };

  return { request };
};
